import { Card, Col, ProgressBar, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { toPersianDigit } from '@/helpers/utils';
import type {
  GeoAnalysisData,
  SeoPriorityTone
} from '@/app/ContentTools/types/aiContentAnalysisApiTypes';
import AiLikenessGauge, {
  getScoreGaugeVerdict,
  SEO_SCORE_GAUGE_ZONES
} from './AiLikenessGauge';
import { OverviewCardTitle } from './OverviewSectionTitle';

type GeoScoreCardProps = {
  data: GeoAnalysisData;
  className?: string;
  showDimensions?: boolean;
  showSummary?: boolean;
};

const ScoreMetric = ({
  label,
  value,
  icon,
  iconClassName,
  helpHtml
}: {
  label: string;
  value: string;
  icon: IconProp;
  iconClassName?: string;
  helpHtml: string;
}) => (
  <div className="d-flex justify-content-between align-items-center bg-100 border rounded-3 px-3 py-2">
    <span className="d-inline-flex align-items-center gap-1 fs--1 text-600 lh-1">
      <FontAwesomeIcon
        icon={icon}
        className={`d-block flex-shrink-0 ${iconClassName || ''}`}
        style={{ width: '0.95em', height: '0.95em' }}
      />
      <span className="lh-1">{label}</span>
      <HelpTooltip placement="top" html={helpHtml} />
    </span>
    <span className="fw-bold fs-0 lh-1">{value}</span>
  </div>
);

const progressVariant = (tone: SeoPriorityTone) => {
  if (tone === 'success') return 'success';
  if (tone === 'info') return 'info';
  if (tone === 'warning') return 'warning';
  return 'danger';
};

const SignalList = ({
  title,
  icon,
  iconClassName,
  badgeBg,
  items,
  helpHtml
}: {
  title: string;
  icon: IconProp;
  iconClassName: string;
  badgeBg: string;
  items: string[];
  helpHtml: string;
}) => {
  if (items.length === 0) return null;

  return (
    <div className="bg-100 border rounded-3 p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="d-inline-flex align-items-center gap-1 fs--1 text-700 fw-semi-bold">
          <FontAwesomeIcon icon={icon} className={iconClassName} />
          {title}
          <HelpTooltip placement="top" html={helpHtml} />
        </span>
        <SubtleBadge bg={badgeBg} className="fs--2">
          {toPersianDigit(`${items.length}`)}
        </SubtleBadge>
      </div>
      <ul className="list-unstyled mb-0">
        {items.map(item => (
          <li
            key={item}
            className="d-flex align-items-start gap-2 fs--1 text-700 mb-1"
          >
            <FontAwesomeIcon
              icon="circle"
              className={`${iconClassName} mt-2 flex-shrink-0`}
              style={{ fontSize: '0.4rem' }}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const GeoScoreSummary = ({
  data,
  metricsClassName = 'mb-3'
}: {
  data: GeoAnalysisData;
  metricsClassName?: string;
}) => {
  const gaugeVerdict = getScoreGaugeVerdict(data.overall_score);

  return (
    <>
      <AiLikenessGauge
        value={data.overall_score}
        displayValue={toPersianDigit(`${Math.round(data.overall_score)}`)}
        zones={SEO_SCORE_GAUGE_ZONES}
      />

      <div className="text-center mb-3 ai-likeness-gauge-caption">
        <div className="fw-bold fs-0">{gaugeVerdict}</div>
        <SubtleBadge bg="success" pill className="mt-2">
          آمادگی GEO از {toPersianDigit('100')}
        </SubtleBadge>
      </div>

      <Row className={`g-2 ${metricsClassName}`}>
        <Col xs={6}>
          <ScoreMetric
            label="نقاط قوت"
            icon="trophy"
            iconClassName="text-success"
            value={toPersianDigit(`${data.top_strengths.length}`)}
            helpHtml="تعداد نقاط قوت کلیدی صفحه برای جستجوی مولد و استنادپذیری."
          />
        </Col>
        <Col xs={6}>
          <ScoreMetric
            label="شکاف‌ها"
            icon="exclamation-triangle"
            iconClassName="text-warning"
            value={toPersianDigit(`${data.top_gaps.length}`)}
            helpHtml="تعداد شکاف‌هایی که شانس دیده شدن در جستجوی مبتنی بر هوش مصنوعی را کم می‌کنند."
          />
        </Col>
      </Row>
    </>
  );
};

const GeoScoreCard = ({
  data,
  className,
  showDimensions = true,
  showSummary = true
}: GeoScoreCardProps) => (
  <Card className={className}>
    <FalconCardHeader
      title={
        <OverviewCardTitle
          icon="globe"
          title="امتیاز GEO"
          helpHtml="این امتیاز آمادگی صفحه برای جستجوی مولد (AI Overview، ChatGPT Search، Perplexity و مشابه) را نشان می‌دهد و احتمال استناد در یک پلتفرم خاص را پیش‌بینی نمی‌کند."
        />
      }
      titleTag="h6"
      titleClass="d-flex align-items-center"
      className="py-2"
      light
    />
    <Card.Body className="d-flex flex-column flex-grow-1">
      <GeoScoreSummary data={data} />

      {showDimensions && data.dimensions.length > 0 ? (
        <div className="border-top pt-3 mb-3">
          <div className="fs--2 text-500 fw-semi-bold mb-2">ابعاد GEO</div>
          <div className="d-flex flex-column gap-2">
            {data.dimensions.map(item => (
              <div key={item.key} className="bg-100 border rounded-3 px-2 py-2">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                  <span className="fs--1 text-700">
                    {item.label}
                    {item.weight ? (
                      <span className="text-500 fs--2 ms-1">
                        ({item.weight})
                      </span>
                    ) : null}
                  </span>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <span className="fs--2 fw-semi-bold text-600">
                      {toPersianDigit(`${item.score}`)} / ۱۰۰
                    </span>
                    <SubtleBadge bg={item.tone} className="fs--2">
                      {item.level}
                    </SubtleBadge>
                  </div>
                </div>
                <ProgressBar
                  now={Math.min(Math.max(item.score, 0), 100)}
                  variant={progressVariant(item.tone)}
                  style={{ height: 6 }}
                  className="bg-200"
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {showSummary &&
      (data.top_strengths.length > 0 || data.top_gaps.length > 0) ? (
        <div className="border-top pt-3">
          <div className="fs--2 text-500 fw-semi-bold mb-2">خلاصه ارزیابی</div>
          <div className="d-flex flex-column gap-2">
            <SignalList
              title="نقاط قوت کلیدی"
              icon="check-circle"
              iconClassName="text-success"
              badgeBg="success"
              items={data.top_strengths}
              helpHtml="مهم‌ترین نشانه‌های آمادگی صفحه برای استخراج و استناد در جستجوی مولد."
            />
            <SignalList
              title="شکاف‌های کلیدی"
              icon="exclamation-circle"
              iconClassName="text-warning"
              badgeBg="warning"
              items={data.top_gaps}
              helpHtml="مهم‌ترین کمبودهایی که شانس دیده شدن در پاسخ‌های هوش مصنوعی را تضعیف می‌کنند."
            />
          </div>
        </div>
      ) : null}
    </Card.Body>
  </Card>
);

export default GeoScoreCard;
